/**
 * The businesses the signed-in person may act for, read FOR ONE IDENTITY
 * (ORDR-204; the reads are ORDR-184's, `firmApi.ts`).
 *
 * MEASURED IN PRODUCTION (2026-10-11). A tab of another app in the same browser
 * changed the shared session's user, and this app went on offering the
 * businesses it had fetched for the previous account. paio-auth then refused
 * the switch, correctly. The list was right when it was read and wrong for the
 * session it was drawn beside.
 *
 * So an answer is kept WITH the key it was read for, and the caller's key
 * carries the session's user id. A different user is a different key: the old
 * answer is never returned for it, and the list is read again.
 */

import { useEffect, useState } from "react";
import type { ApiClient } from "../api/client.js";
import { loadMemberships, loadReach, withReach, type BusinessesResult } from "./firmApi.js";

/**
 * The list for `key`, or `null` while it is being read. A `null` key asks
 * nothing: there is no signed-in person to read it for.
 */
export function useBusinesses(client: ApiClient, key: string | null): BusinessesResult | null {
  const [answer, setAnswer] = useState<{ key: string; result: BusinessesResult } | null>(null);

  useEffect(() => {
    if (key === null) {
      return;
    }
    let live = true;
    void Promise.all([loadMemberships(client), loadReach(client)]).then(([held, reach]) => {
      if (live) {
        setAnswer({ key, result: withReach(held, reach) });
      }
    });
    return () => {
      live = false;
    };
  }, [client, key]);

  return answer !== null && answer.key === key ? answer.result : null;
}
